import {
  num,
  safeDiv,
  type CalculatorDefinition,
  type CalculatorOutput,
} from "./types";

/**
 * Side-hustle break-even.
 *
 * Answers the two questions that decide whether a side project is worth
 * continuing: how many units a month clear the fixed costs, and what the thing
 * pays per hour once your own time is priced honestly.
 */
export const breakEvenCalculator: CalculatorDefinition = {
  kind: "break-even",
  slug: "side-hustle-break-even",
  name: "Side-Hustle Break-Even",
  nameKo: "부업 손익분기 계산기",
  summary:
    "Units per month to cover costs, months to recoup your startup spend, and what the whole thing actually pays per hour.",
  interpretation:
    "Break-even volume falls out of contribution margin — price minus variable cost — divided into fixed costs. The number most people avoid computing is the last one: effective hourly rate. A hustle netting $600/month on 15 hours a week pays under $10/hour, which is the real comparison, not $600 against zero.",
  disclaimer:
    "Educational model. Assumes stable pricing and linear variable costs; real demand curves and seasonality will move these numbers.",
  relatedCaseStudySlugs: ["notion-template-catalog", "pressure-washing-weekend"],

  fields: [
    {
      id: "startupCost",
      label: "One-time startup cost",
      kind: "currency",
      group: "Setup",
      defaultValue: 3200,
      min: 0,
      step: 100,
      help: "Equipment, licences, initial inventory, the domain you already regret buying.",
    },
    {
      id: "monthlyFixedCosts",
      label: "Monthly fixed costs",
      kind: "currency",
      group: "Setup",
      defaultValue: 340,
      min: 0,
      step: 10,
      help: "Software, insurance, storage, subscriptions — costs you pay at zero sales.",
    },

    {
      id: "pricePerUnit",
      label: "Price per unit",
      kind: "currency",
      group: "Unit economics",
      defaultValue: 145,
      min: 0,
      step: 5,
      help: "Per job, per subscription, per product — whatever one sale means for you.",
    },
    {
      id: "variableCostPerUnit",
      label: "Variable cost per unit",
      kind: "currency",
      group: "Unit economics",
      defaultValue: 38,
      min: 0,
      step: 1,
      help: "Materials, payment fees, fulfilment, per-sale ad spend.",
    },
    {
      id: "unitsPerMonth",
      label: "Units sold per month",
      kind: "number",
      group: "Unit economics",
      defaultValue: 12,
      min: 0,
      step: 1,
      help: "Your current or realistically expected volume.",
    },

    {
      id: "hoursPerWeek",
      label: "Your hours per week",
      kind: "number",
      group: "Your time",
      defaultValue: 9,
      min: 0,
      max: 168,
      step: 0.5,
      help: "Include admin, invoicing, and customer messages, not just the billable work.",
    },
    {
      id: "opportunityRate",
      label: "Your hourly opportunity cost",
      kind: "currency",
      group: "Your time",
      defaultValue: 45,
      min: 0,
      step: 5,
      help: "What that hour is otherwise worth — overtime, freelance rate, or whatever you would genuinely choose instead.",
    },
    {
      id: "recoupMonths",
      label: "Target payback window",
      kind: "months",
      group: "Your time",
      defaultValue: 12,
      min: 1,
      max: 120,
      step: 1,
      help: "How quickly you want the startup cost back. Sets the volume target below.",
    },
  ],

  compute: (values) => {
    const startup = num(values, "startupCost");
    const fixed = num(values, "monthlyFixedCosts");
    const price = num(values, "pricePerUnit");
    const variable = num(values, "variableCostPerUnit");
    const units = num(values, "unitsPerMonth");
    const hoursWeek = num(values, "hoursPerWeek");
    const oppRate = num(values, "opportunityRate");
    const recoupWindow = Math.max(num(values, "recoupMonths", 12), 1);

    const contribution = price - variable;
    const contributionMargin = safeDiv(contribution, price);

    const revenue = price * units;
    const variableTotal = variable * units;
    const monthlyProfit = revenue - variableTotal - fixed;

    const breakEvenUnits = contribution > 0 ? safeDiv(fixed, contribution) : null;
    const breakEvenRevenue = breakEvenUnits == null ? null : breakEvenUnits * price;

    // Volume needed to also retire the startup cost inside the target window.
    const targetUnits =
      contribution > 0 ? safeDiv(fixed + startup / recoupWindow, contribution) : null;

    const monthsToRecoup = monthlyProfit > 0 ? safeDiv(startup, monthlyProfit) : null;

    const monthlyHours = hoursWeek * 4.333;
    const effectiveHourly = monthlyHours > 0 ? safeDiv(monthlyProfit, monthlyHours) : null;
    const opportunityCost = monthlyHours * oppRate;
    const profitAfterTime = monthlyProfit - opportunityCost;

    const marginOfSafety =
      breakEvenUnits != null && units > 0
        ? safeDiv(units - breakEvenUnits, units)
        : null;

    const outputs: CalculatorOutput[] = [
      {
        id: "breakEvenUnits",
        label: "Break-even volume",
        value: breakEvenUnits ?? 0,
        format: "number-1dp",
        tone: "neutral",
        primary: true,
        note:
          breakEvenUnits == null
            ? "Price does not exceed variable cost — no volume breaks even."
            : `Units per month to cover ${"$"}${fixed.toLocaleString()} of fixed costs.`,
      },
      {
        id: "monthlyProfit",
        label: "Monthly profit",
        value: monthlyProfit,
        format: "currency",
        tone: monthlyProfit >= 0 ? "currency" : "risk",
        primary: true,
        note: `At ${units} units per month.`,
      },
      {
        id: "effectiveHourly",
        label: "Effective hourly rate",
        value: effectiveHourly ?? 0,
        format: "currency-exact",
        tone:
          effectiveHourly == null
            ? "neutral"
            : effectiveHourly >= oppRate
              ? "currency"
              : "cost",
        primary: true,
        note:
          effectiveHourly == null
            ? "Add your hours to see this."
            : effectiveHourly >= oppRate
              ? "Beats what your time is otherwise worth."
              : `Below your ${"$"}${oppRate}/hr opportunity cost.`,
      },
      {
        id: "contribution",
        label: "Contribution per unit",
        value: contribution,
        format: "currency-exact",
        tone: contribution > 0 ? "currency" : "risk",
        note: "Price minus variable cost — what each sale contributes to fixed costs.",
      },
      {
        id: "contributionMargin",
        label: "Contribution margin",
        value: (contributionMargin ?? 0) * 100,
        format: "percent",
        tone: "neutral",
      },
      {
        id: "breakEvenRevenue",
        label: "Break-even revenue",
        value: breakEvenRevenue ?? 0,
        format: "currency",
        tone: "neutral",
      },
      {
        id: "targetUnits",
        label: `Volume to repay setup in ${recoupWindow} mo`,
        value: targetUnits ?? 0,
        format: "number-1dp",
        tone: "neutral",
        note: "Covers fixed costs and amortises the startup spend over your target window.",
      },
      {
        id: "monthsToRecoup",
        label: "Months to recoup startup cost",
        value: monthsToRecoup ?? 0,
        format: "months",
        tone: "neutral",
        note:
          monthsToRecoup == null
            ? "Never, at current volume."
            : undefined,
      },
      {
        id: "monthlyRevenue",
        label: "Monthly revenue",
        value: revenue,
        format: "currency",
        tone: "neutral",
      },
      {
        id: "profitAfterTime",
        label: "Profit after your time",
        value: profitAfterTime,
        format: "currency",
        tone: profitAfterTime >= 0 ? "currency" : "risk",
        note: "Monthly profit minus what those hours are otherwise worth to you.",
      },
      {
        id: "marginOfSafety",
        label: "Margin of safety",
        value: (marginOfSafety ?? 0) * 100,
        format: "percent",
        tone:
          marginOfSafety == null
            ? "neutral"
            : marginOfSafety >= 0.3
              ? "currency"
              : "cost",
        note: "How far volume can fall before you stop breaking even.",
      },
    ];

    const warnings: string[] = [];
    if (contribution <= 0 && price > 0) {
      warnings.push(
        "Every sale loses money before fixed costs. Volume makes this worse, not better — the price or the variable cost has to change.",
      );
    }
    if (breakEvenUnits != null && units > 0 && units < breakEvenUnits) {
      warnings.push(
        `Current volume is below break-even. You need about ${Math.ceil(breakEvenUnits)} units a month just to hold even.`,
      );
    }
    if (profitAfterTime < 0 && monthlyProfit > 0) {
      warnings.push(
        "Profitable on paper, but the hours are worth more elsewhere. That is fine if you are building an asset — not if you are buying yourself a low-wage job.",
      );
    }
    if (hoursWeek === 0) {
      warnings.push(
        "No hours entered, so the hourly figures are omitted. Almost nothing runs on zero hours.",
      );
    }

    return { outputs, warnings };
  },
};
